import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatusPill from "@/components/StatusPill";
import Analytics from "@/components/Analytics";
import Roster from "@/components/Roster";
import { DeliveryPill } from "@/components/AdminDistribution";
import ReleaseCalendar from "@/components/ReleaseCalendar";
import Statements from "@/components/Statements";
import PayoutCard from "@/components/PayoutCard";
import ShareSheet from "@/components/ShareSheet";
import { toast } from "sonner";
import Seo from "@/lib/seo";
import { UploadSimple, MusicNotes, Plus, LinkSimple, ArrowUpRight, DownloadSimple, Bell, Prohibit } from "@phosphor-icons/react";

const TIER_NAMES = { asylum_starter: "Starter", asylum_pro: "Pro", asylum_label: "Label" };
const VINYL_IMG = "https://images.unsplash.com/photo-1603048588665-791ca8aea617?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHx2aW55bCUyMHJlY29yZCUyMHBsYXllciUyMHR1cm50YWJsZSUyMGRhcmt8ZW58MHx8fHwxNzg5NDU2NDgyfDA&ixlib=rb-4.1.0&q=85";

const copyPresave = (slug) => {
  navigator.clipboard.writeText(`${window.location.origin}/presave/${slug}`);
  toast.success("Pre-save link copied");
};

export default function Dashboard() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/releases")
      .then(({ data }) => setReleases(data.releases))
      .finally(() => setLoading(false));
  }, []);

  const isActive = user?.subscription_status === "active";
  const isLabel = isActive && user?.subscription_tier === "asylum_label" && !user?.label_id;

  const requestTakedown = async (r) => {
    const reason = window.prompt(`Why do you want "${r.title}" taken down from all stores?`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/releases/${r.id}/takedown-request`, { reason: reason.trim() });
      toast.success("Takedown request sent to Asylum for approval");
      const res = await api.get("/releases"); setReleases(res.data.releases);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not request takedown"); }
  };

  const notifyFans = async (id) => {
    try {
      const { data } = await api.post(`/releases/${id}/notify-fans`, { origin_url: window.location.origin });
      toast.success(`Release-day email sent to ${data.sent} fan${data.sent === 1 ? "" : "s"}`);
      const res = await api.get("/releases"); setReleases(res.data.releases);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };

  return (
    <>
      <Seo title="Dashboard" path="/dashboard" noindex description="Artist dashboard." />
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-3">— Dashboard</div>
            <h1 className="font-display text-4xl lg:text-6xl uppercase tracking-tighter" data-testid="dashboard-heading">
              {user?.display_name || "Artist"}<span className="text-red-600">.</span>
            </h1>
            <p className="text-zinc-500 mt-3 text-sm">{user?.label_name ? <>Artist on the <span className="text-white">{user.label_name}</span> roster. Your releases and royalty share below.</> : "Manage your releases, subscription, and royalties."}</p>
          </div>
          <div className="lg:col-span-3"><PayoutCard /></div>
          <div className="lg:col-span-3 border border-zinc-800 p-6" data-testid="subscription-card">
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Subscription</div>
            <div className="font-display text-2xl uppercase tracking-tight mb-1">
              {isActive ? TIER_NAMES[user.subscription_tier] : "No Plan"}
            </div>
            <div className={`text-xs tracking-[0.2em] uppercase mb-4 ${isActive ? "text-green-500" : "text-yellow-500"}`}>
              {isActive ? "● Active" : "● Inactive"}
            </div>
            {!isActive ? (
              <Link to="/pricing" data-testid="dashboard-upgrade-cta" className="w-full inline-flex justify-center bg-red-600 text-white py-3 text-xs tracking-[0.2em] uppercase btn-brutal btn-brutal-red">Choose Plan</Link>
            ) : (
              <Link to="/pricing" data-testid="dashboard-manage-plan" className="w-full inline-flex justify-center border border-zinc-700 py-3 text-xs tracking-[0.2em] uppercase btn-brutal hover:border-white">Manage Plan</Link>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-6">
            <h2 className="font-display text-2xl uppercase tracking-tight">Your Releases</h2>
            <span className="text-xs text-zinc-500">{releases.length} total</span>
          </div>
          <button
            onClick={() => nav("/upload")}
            disabled={!isActive}
            data-testid="new-release-btn"
            className="bg-red-600 text-white px-6 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 btn-brutal btn-brutal-red disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={14} weight="bold" /> New Release
          </button>
        </div>

        {loading ? (
          <div className="border border-zinc-800 p-16 text-center text-zinc-500 text-xs tracking-widest">LOADING...</div>
        ) : releases.length === 0 ? (
          <div className="border border-zinc-800 grid md:grid-cols-2 overflow-hidden" data-testid="empty-releases">
            <div className="aspect-video md:aspect-auto relative">
              <img src={VINYL_IMG} alt="" className="w-full h-full object-cover grayscale opacity-60" />
            </div>
            <div className="p-12 flex flex-col justify-center">
              <MusicNotes size={40} weight="duotone" className="text-red-600 mb-6" />
              <div className="font-display text-3xl uppercase tracking-tight mb-3">No drops yet.</div>
              <p className="text-zinc-500 text-sm mb-6">Push your first record to Spotify, Apple Music, and 148 other platforms.</p>
              {isActive ? (
                <Link to="/upload" className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 text-xs tracking-[0.2em] uppercase btn-brutal btn-brutal-red w-fit" data-testid="empty-upload-cta">
                  <UploadSimple size={14} weight="bold" /> Upload Release
                </Link>
              ) : (
                <Link to="/pricing" className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 text-xs tracking-[0.2em] uppercase btn-brutal btn-brutal-red w-fit" data-testid="empty-pricing-cta">
                  Pick a Plan First
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div className="border border-zinc-800" data-testid="releases-table">
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-800 text-[10px] tracking-[0.2em] uppercase text-zinc-500 bg-zinc-950">
              <div className="col-span-4">Title</div>
              <div className="col-span-2">Artist</div>
              <div className="col-span-2">Release Date</div>
              <div className="col-span-2">Pre-Save</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {releases.map((r) => (
              <div key={r.id} data-testid={`release-row-${r.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 last:border-b-0 row-hover items-center text-sm">
                <div className="col-span-4 truncate">
                  <div className="font-medium truncate flex items-center gap-2">
                    {r.title}
                    {r.splits?.length > 0 && <span className="text-[9px] tracking-widest uppercase border border-zinc-700 px-1.5 py-0.5 text-zinc-400" data-testid={`split-badge-${r.id}`}>{r.is_owner ? `${r.my_percent}% · ${r.splits.length} split${r.splits.length > 1 ? "s" : ""}` : `${r.my_percent}% share`}</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    {r.release_type || "single"} · {r.tracks?.length || 1} track{(r.tracks?.length || 1) > 1 ? "s" : ""} · {r.genre}{!r.is_owner && " · via label"}
                  </div>
                </div>
                <div className="col-span-2 text-zinc-400 truncate">{r.artist_name}</div>
                <div className="col-span-2 text-zinc-500 text-xs">{r.release_date}</div>
                <div className="col-span-2">
                  {r.status === "rejected" ? <span className="text-zinc-600 text-xs">—</span> : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => copyPresave(r.presave_slug)} data-testid={`copy-presave-${r.id}`}
                        className="text-[10px] tracking-[0.15em] uppercase border border-zinc-700 px-2 py-1 hover:border-red-600 hover:text-red-500 transition-colors inline-flex items-center gap-1">
                        <LinkSimple size={10} /> Link
                      </button>
                      <ShareSheet slug={r.presave_slug} title={r.title} artist={r.artist_name} compact />
                      <Link to={`/presave/${r.presave_slug}`} data-testid={`view-presave-${r.id}`} className="text-zinc-500 hover:text-white"><ArrowUpRight size={12} /></Link>
                      <span className="text-[10px] font-mono text-zinc-400" data-testid={`presave-count-${r.id}`}>{r.presave_count || 0} fans</span>
                      {r.is_owner && r.presave_count > 0 && (
                        <a href={`${API}/releases/${r.id}/fans.csv?auth=${localStorage.getItem("asylum_token")}`} download data-testid={`export-fans-${r.id}`} title="Download fan emails (CSV)"
                          className="text-zinc-500 hover:text-white"><DownloadSimple size={12} /></a>
                      )}
                      {r.is_owner && r.status === "live" && (
                        r.fans_notified_at
                          ? <span className="text-[9px] tracking-widest uppercase text-green-500" data-testid={`fans-notified-${r.id}`}>✓ Fans emailed</span>
                          : <button onClick={() => notifyFans(r.id)} data-testid={`notify-fans-${r.id}`} className="text-[9px] tracking-widest uppercase text-red-500 hover:text-white inline-flex items-center gap-1"><Bell size={10} weight="bold" /> Email fans</button>
                      )}
                      {r.is_owner && (r.status === "live" || r.status === "approved") && (
                        r.takedown?.status && r.takedown.status !== "rejected"
                          ? <span className="text-[9px] tracking-widest uppercase text-zinc-500" data-testid={`takedown-status-${r.id}`}>Takedown {r.takedown.status}</span>
                          : <button onClick={() => requestTakedown(r)} data-testid={`request-takedown-${r.id}`} className="text-[9px] tracking-widest uppercase text-zinc-500 hover:text-red-500 inline-flex items-center gap-1"><Prohibit size={10} weight="bold" /> Takedown</button>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  <StatusPill status={r.status} />{r.delivery?.status && <div className="mt-1"><DeliveryPill delivery={r.delivery} /></div>}
                  {r.delivery?.issue && (
                    <div className="mt-1 inline-flex max-w-full items-start gap-1 text-left" title={r.delivery.issue.message} data-testid={`delivery-issue-${r.id}`}>
                      <span className="text-[9px] tracking-widest uppercase border border-red-600 bg-red-600/10 text-red-500 px-1.5 py-0.5 shrink-0">Delivery issue</span>
                      <span className="text-[10px] text-zinc-500 truncate">{r.delivery.issue.stores?.length ? r.delivery.issue.stores.join(", ") : r.delivery.issue.message}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-14">
          {isLabel && <Roster />}
          <div className="mb-14"><ReleaseCalendar title="Your Release Calendar" /></div>
          <Analytics />
          <div className="mt-14"><Statements /></div>
        </div>
      </div>
      <Footer />
    </>
  );
}
